import { User } from '../types';
import { storage, db } from './firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { collection, getDocs, getDoc, query, where, limit as firestoreLimit, arrayUnion, arrayRemove, updateDoc, doc } from 'firebase/firestore';
import { getUserById as getFirebaseUserById, updateUserProfile as updateFirebaseUserProfile } from './firebaseService';

export class UserService {
  // Obtenir un utilisateur par ID depuis Firestore
  static async getUserById(userId: string): Promise<User | null> {
    try {
      return await getFirebaseUserById(userId);
    } catch (error) {
      console.error('Erreur récupération utilisateur par ID:', error);
      return null;
    }
  }

  // Obtenir plusieurs utilisateurs par IDs
  static async getUsersByIds(userIds: string[]): Promise<User[]> {
    try {
      const users: User[] = [];
      for (const userId of userIds) {
        const user = await getFirebaseUserById(userId);
        if (user) users.push(user);
      }
      return users;
    } catch (error) {
      console.error('Erreur récupération utilisateurs par IDs:', error);
      return [];
    }
  }

  // Rechercher des utilisateurs par nom d'utilisateur (version simplifiée)
  static async searchUsers(query: string): Promise<User[]> {
    // TODO: Implémenter la recherche Firestore
    console.warn('searchUsers: À implémenter avec Firestore');
    return [];
  }

  // Obtenir les utilisateurs suggérés
  static async getSuggestedUsers(currentUserId: string, maxResults: number = 5): Promise<User[]> {
    try {
      // Récupérer l'utilisateur actuel pour connaître ses abonnements
      const currentUser = await getFirebaseUserById(currentUserId);
      if (!currentUser) return [];

      // Récupérer les utilisateurs que l'utilisateur ne suit pas encore
      const usersRef = collection(db, 'users');
      const q = query(
        usersRef,
        where('id', '!=', currentUserId),
        firestoreLimit(maxResults * 2) // Récupérer plus d'utilisateurs pour filtrer ensuite
      );

      const querySnapshot = await getDocs(q);
      const suggestedUsers: User[] = [];

      // Filtrer pour ne pas inclure les utilisateurs déjà suivis
      const alreadyFollowing = new Set(currentUser.following || []);

      querySnapshot.forEach(doc => {
        const userData = { id: doc.id, ...doc.data() } as User;
        if (!alreadyFollowing.has(userData.id)) {
          suggestedUsers.push(userData);
        }
      });

      // Limiter au nombre demandé
      return suggestedUsers.slice(0, maxResults);
    } catch (error) {
      console.error('Erreur récupération utilisateurs suggérés:', error);
      return [];
    }
  }

  // Follow un utilisateur
  static async followUser(currentUserId: string, targetUserId: string): Promise<boolean> {
    try {
      // Vérifier que les utilisateurs existent
      const currentUser = await getFirebaseUserById(currentUserId);
      const targetUser = await getFirebaseUserById(targetUserId);

      if (!currentUser || !targetUser) {
        console.error('Un des utilisateurs n\'existe pas');
        return false;
      }

      // Vérifier que l'utilisateur ne se suit pas lui-même
      if (currentUserId === targetUserId) {
        console.error('Un utilisateur ne peut pas se suivre lui-même');
        return false;
      }

      // Vérifier si l'utilisateur suit déjà la cible
      const alreadyFollowing = (currentUser.following || []).includes(targetUserId);
      if (alreadyFollowing) {
        console.log('Déjà suivi');
        return true; // Déjà suivi, considérer comme succès
      }

      // Mettre à jour le following de l'utilisateur actuel
      await updateDoc(doc(db, 'users', currentUserId), {
        following: arrayUnion(targetUserId),
        followingCount: (currentUser.followingCount || 0) + 1
      });

      // Mettre à jour les followers de la cible
      await updateDoc(doc(db, 'users', targetUserId), {
        followers: arrayUnion(currentUserId),
        followersCount: (targetUser.followersCount || 0) + 1
      });

      return true;
    } catch (error) {
      console.error('Erreur follow utilisateur:', error);
      return false;
    }
  }

  // Unfollow un utilisateur
  static async unfollowUser(currentUserId: string, targetUserId: string): Promise<boolean> {
    try {
      // Vérifier que les utilisateurs existent
      const currentUser = await getFirebaseUserById(currentUserId);
      const targetUser = await getFirebaseUserById(targetUserId);

      if (!currentUser || !targetUser) {
        console.error('Un des utilisateurs n\'existe pas');
        return false;
      }

      // Vérifier si l'utilisateur suit déjà la cible
      const isFollowing = (currentUser.following || []).includes(targetUserId);
      if (!isFollowing) {
        console.log('Pas encore suivi');
        return true; // Déjà non suivi, considérer comme succès
      }

      // Mettre à jour le following de l'utilisateur actuel
      await updateDoc(doc(db, 'users', currentUserId), {
        following: arrayRemove(targetUserId),
        followingCount: Math.max((currentUser.followingCount || 0) - 1, 0)
      });

      // Mettre à jour les followers de la cible
      await updateDoc(doc(db, 'users', targetUserId), {
        followers: arrayRemove(currentUserId),
        followersCount: Math.max((targetUser.followersCount || 0) - 1, 0)
      });

      return true;
    } catch (error) {
      console.error('Erreur unfollow utilisateur:', error);
      return false;
    }
  }

  // Vérifier si un utilisateur suit un autre
  static async isFollowing(currentUserId: string, targetUserId: string): Promise<boolean> {
    try {
      const currentUser = await getFirebaseUserById(currentUserId);

      if (!currentUser) {
        return false;
      }

      // Vérifier si targetUserId est dans la liste des following
      return (currentUser.following || []).includes(targetUserId);
    } catch (error) {
      console.error('Erreur vérification following:', error);
      return false;
    }
  }

  // Récupérer les followers (version réelle)
  static async getFollowers(userId: string): Promise<User[]> {
    try {
      console.log('📊 Récupération des followers réels pour', userId);

      // Récupérer le document utilisateur
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (!userDoc.exists()) {
        console.log('👤 Utilisateur non trouvé:', userId);
        return [];
      }

      const userData = userDoc.data();
      const followerIds = userData.followers || [];
      
      if (followerIds.length === 0) {
        console.log('📭 Aucun follower pour cet utilisateur');
        return [];
      }

      // Récupérer les données de chaque follower
      const followers: User[] = [];
      for (const followerId of followerIds) {
        try {
          const followerDoc = await getDoc(doc(db, 'users', followerId));
          if (followerDoc.exists()) {
            const followerData = followerDoc.data();
            followers.push({
              id: followerDoc.id,
              walletAddress: followerData.walletAddress,
              username: followerData.username,
              displayName: followerData.displayName || '',
              bio: followerData.bio || '',
              profilePicture: followerData.profilePicture || '🐱‍🚀',
              bannerImage: followerData.bannerImage || '',
              xp: followerData.xp || 0,
              level: followerData.level || 1,
              badges: followerData.badges || [],
              socialLinks: followerData.socialLinks || {},
              isVerified: followerData.isVerified || false,
              role: followerData.role || 'user',
              following: followerData.following || [],
              followers: followerData.followers || [],
              followersCount: followerData.followersCount || 0,
              followingCount: followerData.followingCount || 0,
              createdAt: followerData.createdAt?.toDate() || new Date(),
              updatedAt: followerData.updatedAt?.toDate() || new Date()
            });
          }
        } catch (error) {
          console.error('❌ Erreur récupération follower:', followerId, error);
        }
      }

      console.log('✅ Followers récupérés:', followers.length);
      return followers;
    } catch (error) {
      console.error('Erreur récupération followers:', error);
      return [];
    }
  }

  // Récupérer les following (version réelle)
  static async getFollowing(userId: string): Promise<User[]> {
    try {
      console.log('📊 Récupération des abonnements réels pour', userId);

      // Récupérer le document utilisateur
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (!userDoc.exists()) {
        console.log('👤 Utilisateur non trouvé:', userId);
        return [];
      }

      const userData = userDoc.data();
      const followingIds = userData.following || [];
      
      if (followingIds.length === 0) {
        console.log('📭 Aucun abonnement pour cet utilisateur');
        return [];
      }

      // Récupérer les données de chaque utilisateur suivi
      const following: User[] = [];
      for (const followingId of followingIds) {
        try {
          const followingDoc = await getDoc(doc(db, 'users', followingId));
          if (followingDoc.exists()) {
            const followingData = followingDoc.data();
            following.push({
              id: followingDoc.id,
              walletAddress: followingData.walletAddress,
              username: followingData.username,
              displayName: followingData.displayName || '',
              bio: followingData.bio || '',
              profilePicture: followingData.profilePicture || '🐱‍🚀',
              bannerImage: followingData.bannerImage || '',
              xp: followingData.xp || 0,
              level: followingData.level || 1,
              badges: followingData.badges || [],
              socialLinks: followingData.socialLinks || {},
              isVerified: followingData.isVerified || false,
              role: followingData.role || 'user',
              following: followingData.following || [],
              followers: followingData.followers || [],
              followersCount: followingData.followersCount || 0,
              followingCount: followingData.followingCount || 0,
              createdAt: followingData.createdAt?.toDate() || new Date(),
              updatedAt: followingData.updatedAt?.toDate() || new Date()
            });
          }
        } catch (error) {
          console.error('❌ Erreur récupération following:', followingId, error);
        }
      }

      console.log('✅ Following récupérés:', following.length);
      return following;
    } catch (error) {
      console.error('Erreur récupération following:', error);
      return [];
    }
  }

  // Mettre à jour le profil utilisateur
  static async updateUserProfile(userId: string, updates: Partial<User>): Promise<User | null> {
    try {
      await updateFirebaseUserProfile(userId, updates);
      return await getFirebaseUserById(userId);
    } catch (error) {
      console.error('Erreur mise à jour profil:', error);
      return null;
    }
  }

  // Upload d'image de profil
  static async uploadProfilePicture(userId: string, file: File): Promise<string | null> {
    try {
      console.log('🔍 Début upload image profil pour', userId);
      console.log('📂 Type du fichier:', file.type, 'Taille:', file.size);
      
      const imageRef = ref(storage, `profiles/${userId}/avatar-${Date.now()}`);
      console.log('🔗 Référence de stockage créée:', imageRef.fullPath);
      
      const snapshot = await uploadBytes(imageRef, file);
      console.log('✅ Image uploadée avec succès');
      
      const url = await getDownloadURL(snapshot.ref);
      console.log('🌐 URL de téléchargement obtenue:', url);
      
      // Mettre à jour le profil utilisateur avec la nouvelle URL
      await updateFirebaseUserProfile(userId, { profilePicture: url });
      console.log('✅ Profil utilisateur mis à jour avec la nouvelle image');
      
      return url;
    } catch (error) {
      console.error('❌ Erreur upload image profil:', error);
      return null;
    }
  }

  // Upload d'image de bannière
  static async uploadBannerImage(userId: string, file: File): Promise<string | null> {
    try {
      console.log('🔍 Début upload bannière pour', userId);
      console.log('📂 Type du fichier:', file.type, 'Taille:', file.size);
      
      const imageRef = ref(storage, `profiles/${userId}/banner-${Date.now()}`);
      console.log('🔗 Référence de stockage créée:', imageRef.fullPath);
      
      const snapshot = await uploadBytes(imageRef, file);
      console.log('✅ Image uploadée avec succès');
      
      const url = await getDownloadURL(snapshot.ref);
      console.log('🌐 URL de téléchargement obtenue:', url);
      
      // Mettre à jour le profil utilisateur avec la nouvelle URL
      await updateFirebaseUserProfile(userId, { bannerImage: url });
      console.log('✅ Profil utilisateur mis à jour avec la nouvelle bannière');
      
      return url;
    } catch (error) {
      console.error('❌ Erreur upload bannière:', error);
      return null;
    }
  }

  // Vérifier si un utilisateur est en ligne (simulation)
  static async isUserOnline(userId: string): Promise<boolean> {
    // TODO: Implémenter avec un système de présence Firestore
    return Math.random() > 0.5;
  }

  // Obtenir les utilisateurs en ligne (simulation)
  static async getOnlineUsers(): Promise<User[]> {
    // TODO: Implémenter avec Firestore
    return [];
  }

  // Ajouter un badge à un utilisateur
  static async addBadge(userId: string, badgeName: string): Promise<boolean> {
    try {
      const user = await getFirebaseUserById(userId);
      if (!user) return false;
      
      const updatedBadges = user.badges || [];
      const newBadge = {
        id: `badge_${Date.now()}`,
        name: badgeName,
        description: `Badge ${badgeName}`,
        icon: '🏆',
        rarity: 'common' as const,
        unlockedAt: new Date()
      };
      
      // Vérifier si le badge n'existe pas déjà
      const existingBadge = updatedBadges.find(b => b.name === badgeName);
      if (!existingBadge) {
        updatedBadges.push(newBadge);
        await updateFirebaseUserProfile(userId, { badges: updatedBadges });
      }
      
      return true;
    } catch (error) {
      console.error('Erreur ajout badge:', error);
      return false;
    }
  }

  // Mettre à jour l'XP et le niveau
  static async updateXP(userId: string, xpGain: number): Promise<boolean> {
    try {
      const user = await getFirebaseUserById(userId);
      if (!user) return false;
      
      const newXp = (user.xp || 0) + xpGain;
      const newLevel = Math.floor(newXp / 100) + 1;
      
      const updates: Partial<User> = { xp: newXp };
      
      if (newLevel > (user.level || 1)) {
        updates.level = newLevel;
        // Ajouter un badge de niveau si milestone
        if (newLevel % 5 === 0) {
          await this.addBadge(userId, `Level ${newLevel} Master`);
        }
      }
      
      await updateFirebaseUserProfile(userId, updates);
      return true;
    } catch (error) {
      console.error('Erreur mise à jour XP:', error);
      return false;
    }
  }

  // Obtenir les stats d'un utilisateur
  static async getUserStats(userId: string): Promise<{
    postsCount: number;
    likesReceived: number;
    commentsCount: number;
    xp: number;
    level: number;
  } | null> {
    try {
      const user = await getFirebaseUserById(userId);
      if (!user) return null;
      
      return {
        postsCount: 0, // À calculer depuis les posts
        likesReceived: 0, // À calculer depuis les likes sur les posts
        commentsCount: 0, // À calculer depuis les commentaires
        xp: user.xp || 0,
        level: user.level || 1
      };
    } catch (error) {
      console.error('Erreur récupération stats:', error);
      return null;
    }
  }
}
